import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Check } from "lucide-react";
import { useEffect, useState } from "react";

import { ApiError } from "@/api/client";
import { Button, Dialog, SelectField, TextField } from "@/components/ui";
import { cn } from "@/lib/cn";
import { formatMoney } from "@/lib/format";
import {
  inventoryRepository,
  type SectionBalance,
  type StockProduct,
} from "@/repositories/catalogueRepository";
import { hierarchyRepository, type SectionOption } from "@/repositories/hierarchyRepository";
import { useAuthStore } from "@/stores/authStore";

import { useScopeKey } from "./useScopeKey";

export type StockAction = "receive" | "adjust" | "count" | "movements";

/**
 * Receiving, adjusting and counting stock.
 *
 * One dialog rather than three, because the three share almost everything: the
 * product, the counter, a quantity, and the same "what will this leave on the
 * shelf?" preview. What differs is one field and the sentence above it.
 *
 * The running preview is the point. A stock adjustment is the operation people
 * get wrong most often — sign errors on a write-off, a count entered as a
 * variance — and showing "48 → 45" before the button is pressed catches that
 * while it is still free.
 */
export function StockDialog({
  action,
  product,
  onClose,
}: {
  action: StockAction;
  product: StockProduct;
  onClose: () => void;
}) {
  if (action === "movements") {
    return <MovementsDialog product={product} onClose={onClose} />;
  }
  return <MovementForm action={action} product={product} onClose={onClose} />;
}

// ═════════════════════════════════════════════════════════════════════════════
// Receive / adjust / count
// ═════════════════════════════════════════════════════════════════════════════
const ADJUSTMENT_REASONS = [
  { value: "ADJUSTMENT", label: "Correction" },
  { value: "WASTAGE", label: "Wastage, breakage or expiry" },
  { value: "RETURN_TO_SUPPLIER", label: "Returned to the supplier" },
] as const;

const COPY = {
  receive: {
    title: "Receive stock",
    verb: "Receive",
    hint: "Goods in from a wholesaler. The cost updates this product's average cost, and does not change its selling price.",
  },
  adjust: {
    title: "Adjust stock",
    verb: "Adjust",
    hint: "A correction, a write-off or a return. Every adjustment needs a reason — an unexplained one is indistinguishable from theft in a shrinkage report.",
  },
  count: {
    title: "Count stock",
    verb: "Save the count",
    hint: "Type what is physically on the shelf. Only the difference is recorded.",
  },
} as const;

function MovementForm({
  action,
  product,
  onClose,
}: {
  action: "receive" | "adjust" | "count";
  product: StockProduct;
  onClose: () => void;
}) {
  const copy = COPY[action];
  const client = useQueryClient();

  const activeSectionId = useAuthStore((s) => s.session?.activeSectionId ?? "");
  const scope = useScopeKey();

  const [sections, setSections] = useState<SectionOption[]>([]);
  const [sectionId, setSectionId] = useState(activeSectionId);
  const [quantity, setQuantity] = useState("");
  const [unitCost, setUnitCost] = useState(product.cost_price ?? "");
  const [reference, setReference] = useState("");
  const [reason, setReason] = useState("");
  const [movementType, setMovementType] = useState<string>("ADJUSTMENT");
  const [done, setDone] = useState<string | null>(null);

  // Where this product's stock actually is. Needed for both the default counter
  // and the preview — see the comment on `defaultSection` below.
  const balances = useQuery({
    queryKey: ["inventory", "balances", product.id, scope],
    queryFn: () => inventoryRepository.balances(product.id),
  });

  useEffect(() => {
    void hierarchyRepository.sectionsForUser().then(setSections);
  }, []);

  useEffect(() => {
    if (sectionId || sections.length === 0 || balances.data === undefined) return;
    setSectionId(defaultSection(sections, balances.data, activeSectionId));
  }, [sectionId, sections, balances.data, activeSectionId]);

  /**
   * The counter this form is about.
   *
   * `product.stock_on_hand` from the table is the **sum across every counter**
   * the caller can see, so previewing against it is wrong the moment a merchant
   * has more than one. Receiving into Vosloorus while the panel reassures you
   * that 48 will become 72 — when Vosloorus holds none of them — is how a
   * delivery ends up on the wrong shelf with the software agreeing.
   */
  const here = balances.data?.find((row) => row.section_id === sectionId);
  const onHand = Number(here?.quantity ?? 0);
  const typed = Number(quantity);
  const valid = quantity.trim() !== "" && !Number.isNaN(typed);

  /** What the shelf will read afterwards. */
  const projected = !valid
    ? null
    : action === "receive"
      ? onHand + Math.abs(typed)
      : action === "count"
        ? typed
        : onHand + typed;

  const submit = useMutation({
    mutationFn: async () => {
      const base = { product_id: product.id, section_id: sectionId };
      if (action === "receive") {
        return inventoryRepository.receive({
          ...base,
          quantity: String(Math.abs(typed)),
          ...(unitCost ? { unit_cost: unitCost } : {}),
          ...(reference ? { reference } : {}),
        });
      }
      if (action === "count") {
        return inventoryRepository.count({ ...base, counted_quantity: String(typed) });
      }
      return inventoryRepository.adjust({
        ...base,
        quantity: String(typed),
        reason,
        movement_type: movementType,
      });
    },
    onSuccess: (result) => {
      void client.invalidateQueries({ queryKey: ["inventory"] });
      setDone(
        "quantity_on_hand" in result && result.quantity_on_hand
          ? `${product.name} is now showing ${Number(result.quantity_on_hand)}.`
          : (result as { detail?: string }).detail ||
              "The count agreed with the system, so nothing was recorded.",
      );
    },
  });

  const blocked =
    !valid ||
    !sectionId ||
    (action === "adjust" && (reason.trim() === "" || typed === 0)) ||
    (action === "receive" && Math.abs(typed) === 0);

  if (done) {
    return (
      <Dialog
        open
        onClose={onClose}
        title={copy.title}
        size="sm"
        footer={
          <Button variant="primary" onClick={onClose}>
            Done
          </Button>
        }
      >
        <p className="flex items-start gap-3 text-sm text-ink-900">
          <Check className="mt-0.5 size-5 shrink-0 text-brand-600" aria-hidden />
          {done}
        </p>
      </Dialog>
    );
  }

  return (
    <Dialog
      open
      onClose={onClose}
      title={copy.title}
      description={product.name}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submit.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={blocked || submit.isPending}
            onClick={() => submit.mutate()}
          >
            {submit.isPending ? "Saving…" : copy.verb}
          </Button>
        </>
      }
    >
      <form
        className="space-y-4"
        onSubmit={(event) => {
          event.preventDefault();
          if (!blocked) submit.mutate();
        }}
      >
        <p className="text-sm leading-relaxed text-ink-500">{copy.hint}</p>

        {sections.length > 1 && (
          <SelectField
            label="Counter"
            hint="Stock is held per counter, so this decides which shelf changes."
            value={sectionId}
            onChange={(event) => setSectionId(event.target.value)}
            options={sections.map((option) => ({
              value: option.sectionId,
              label: sections.some((other) => other.branchId !== option.branchId)
                ? `${option.branchName} · ${option.sectionName}`
                : option.sectionName,
            }))}
          />
        )}

        <TextField
          label={
            action === "receive"
              ? "Quantity received"
              : action === "count"
                ? "Counted on the shelf"
                : "Change in quantity"
          }
          hint={
            action === "adjust"
              ? "Negative to take stock off the shelf — “-3” for three broken bottles."
              : undefined
          }
          // `decimal` rather than `numeric`: a butchery weighs 0.75 kg, and the
          // numeric keypad on Android has no decimal point.
          inputMode="decimal"
          autoComplete="off"
          value={quantity}
          onChange={(event) => setQuantity(event.target.value)}
        />

        {action === "receive" && (
          <>
            <TextField
              label="Cost per unit"
              hint="Blends into the average cost. Leave as it is if the price has not changed."
              inputMode="decimal"
              value={unitCost}
              onChange={(event) => setUnitCost(event.target.value)}
            />
            <TextField
              label="Reference"
              hint="Optional — a delivery note or invoice number."
              value={reference}
              onChange={(event) => setReference(event.target.value)}
            />
          </>
        )}

        {action === "adjust" && (
          <>
            <SelectField
              label="Type"
              value={movementType}
              onChange={(event) => setMovementType(event.target.value)}
              options={ADJUSTMENT_REASONS.map((entry) => ({
                value: entry.value,
                label: entry.label,
              }))}
            />
            <TextField
              label="Reason"
              hint="Required. Say what happened, in the words you would use to explain it later."
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
          </>
        )}

        <Preview
          onHand={onHand}
          projected={projected}
          unit={product.unit?.plural_name ?? "units"}
          loading={balances.isLoading}
        />

        {/* Where the rest of it is. "This shelf has none" is alarming until you
            can see that the other counter has forty. */}
        {balances.data && balances.data.some((row) => row.section_id !== sectionId) && (
          <p className="text-xs text-ink-500">
            Elsewhere:{" "}
            {balances.data
              .filter((row) => row.section_id !== sectionId && Number(row.quantity) !== 0)
              .map((row) => `${row.section_name} ${Number(row.quantity)}`)
              .join(" · ") || "none"}
          </p>
        )}

        {action === "receive" && valid && unitCost && (
          <p className="text-xs text-ink-500">
            Adds {formatMoney(String(Math.abs(typed) * Number(unitCost)))} of stock at cost.
          </p>
        )}

        {submit.isError && <SubmitError error={submit.error} />}

        {/* A submit button so Enter works in the fields. The visible action is in
            the footer, outside the form element. */}
        <button type="submit" className="sr-only" tabIndex={-1}>
          {copy.verb}
        </button>
      </form>
    </Dialog>
  );
}

/**
 * Pick the counter this form should open on.
 *
 * In order: the one the top bar has selected, then the only one holding stock of
 * this product, then the merchant's own default counter. Never simply the first
 * in the list — that is alphabetical, and it is how a Katlehong delivery lands
 * on the Vosloorus shelf.
 */
function defaultSection(
  sections: SectionOption[],
  balances: SectionBalance[],
  activeSectionId: string,
): string {
  if (activeSectionId && sections.some((s) => s.sectionId === activeSectionId)) {
    return activeSectionId;
  }

  const holding = balances.filter((row) => Number(row.quantity) !== 0);
  if (holding.length === 1 && sections.some((s) => s.sectionId === holding[0]!.section_id)) {
    return holding[0]!.section_id;
  }

  return sections.find((s) => s.isDefault)?.sectionId ?? sections[0]?.sectionId ?? "";
}

function Preview({
  onHand,
  projected,
  unit,
  loading,
}: {
  onHand: number;
  projected: number | null;
  unit: string;
  loading: boolean;
}) {
  const negative = projected !== null && projected < 0;

  return (
    <div
      className={cn(
        "flex items-center justify-between gap-4 rounded-card border px-4 py-3",
        negative ? "border-warn-500/30 bg-warn-50" : "border-line bg-canvas",
      )}
    >
      <div>
        <p className="text-xs uppercase tracking-wide text-ink-500">On this counter</p>
        <p className="text-lg font-semibold tabular-nums text-ink-900">
          {loading ? "…" : onHand} <span className="text-sm font-normal text-ink-500">{unit}</span>
        </p>
      </div>

      {projected !== null && (
        <>
          <span aria-hidden className="text-ink-400">
            →
          </span>
          <div className="text-right">
            <p className="text-xs uppercase tracking-wide text-ink-500">After</p>
            <p
              className={cn(
                "text-lg font-semibold tabular-nums",
                negative ? "text-warn-700" : "text-ink-900",
              )}
            >
              {projected} <span className="text-sm font-normal text-ink-500">{unit}</span>
            </p>
          </div>
        </>
      )}
    </div>
  );
}

function SubmitError({ error }: { error: unknown }) {
  if (error instanceof ApiError && error.code === "INSUFFICIENT_STOCK") {
    return (
      <p className="flex items-start gap-2 rounded-card border border-danger-500/30 bg-danger-50 px-4 py-3 text-sm text-danger-700">
        <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden />
        {error.detail} Your business is set to block negative stock — a manager can change that in
        Settings.
      </p>
    );
  }

  return (
    <p className="rounded-card border border-danger-500/30 bg-danger-50 px-4 py-3 text-sm text-danger-700">
      {error instanceof ApiError ? error.detail : "That could not be saved. Please try again."}
    </p>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// The ledger for one product
// ═════════════════════════════════════════════════════════════════════════════
const MOVEMENT_LABEL: Record<string, string> = {
  OPENING: "Opening balance",
  RECEIPT: "Received",
  SALE: "Sale",
  REFUND: "Customer return",
  ADJUSTMENT: "Adjusted",
  COUNT: "Stock count",
  TRANSFER_IN: "Transferred in",
  TRANSFER_OUT: "Transferred out",
  WASTAGE: "Wastage",
  RETURN_TO_SUPPLIER: "Returned to supplier",
};

/**
 * Why this figure is what it is.
 *
 * The whole justification for an append-only ledger, made visible: a shopkeeper
 * asking "why does it say 12?" gets the arithmetic rather than an assurance.
 */
function MovementsDialog({ product, onClose }: { product: StockProduct; onClose: () => void }) {
  const movements = useQuery({
    queryKey: ["inventory", "movements", product.id], // not scoped: the ledger is per product
    queryFn: () => inventoryRepository.movements(product.id),
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title="Stock movements"
      description={product.name}
      footer={
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      }
    >
      {movements.isLoading ? (
        <p className="py-8 text-center text-sm text-ink-500">Loading…</p>
      ) : movements.data && movements.data.results.length > 0 ? (
        <ol className="divide-y divide-line">
          {movements.data.results.map((movement) => {
            const amount = Number(movement.quantity);
            return (
              <li key={movement.id} className="flex items-start gap-3 py-3">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-ink-900">
                    {MOVEMENT_LABEL[movement.movement_type] ?? movement.movement_type}
                  </p>
                  {movement.reason && <p className="text-xs text-ink-500">{movement.reason}</p>}
                  <p className="text-xs text-ink-400">
                    {new Date(movement.occurred_at).toLocaleString("en-ZA", {
                      day: "2-digit",
                      month: "short",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                    {movement.by && ` · ${movement.by}`}
                  </p>
                </div>
                <div className="shrink-0 text-right">
                  <p
                    className={cn(
                      "text-sm font-semibold tabular-nums",
                      amount >= 0 ? "text-brand-600" : "text-danger-700",
                    )}
                  >
                    {amount > 0 ? "+" : ""}
                    {amount}
                  </p>
                  {/* The running balance, so the column reads like a bank
                      statement and a discrepancy localises to one line. */}
                  <p className="text-xs tabular-nums text-ink-500">
                    → {Number(movement.balance_after)}
                  </p>
                </div>
              </li>
            );
          })}
        </ol>
      ) : (
        <p className="py-8 text-center text-sm text-ink-500">
          Nothing has moved for this product yet.
        </p>
      )}
    </Dialog>
  );
}
